/**
 * Guarded User Timing wrapper for post-fetch profiling of
 * PortfolioShow's synchronous processing pipeline.
 *
 * The browser Performance API is available in every supported target, but
 * jsdom does not implement `mark`/`measure`/`clear*`. The helpers below no-op
 * when any required method is unavailable so they can be called
 * unconditionally from production code and unit tests.
 *
 * The helpers accept static string names only. Callers must not include
 * sensitive values (portfolio UUIDs, meter IDs, timestamps) — the sanitizer
 * rejects anything that is not a non-empty ASCII identifier-style string.
 * Rejected calls become no-ops so instrumentation never throws or logs data.
 */

import logger from 'src/util/logger';

const NAME_PATTERN = /^[a-zA-Z0-9:_.-]+$/;

/**
 * Return the browser performance object when User Timing is fully supported.
 * @returns {Performance | null} The `performance` object, or null when the
 *   User Timing API is unavailable (e.g. in jsdom).
 */
const getPerformance = () => {
  if (typeof performance === 'undefined' || !performance) return null;
  if (typeof performance.mark !== 'function') return null;
  if (typeof performance.measure !== 'function') return null;
  if (typeof performance.clearMarks !== 'function') return null;
  if (typeof performance.clearMeasures !== 'function') return null;
  return performance;
};

/**
 * Validate that a mark or measure name is a static, non-sensitive identifier.
 * @param {*} name - Candidate name.
 * @returns {boolean} True when the name is a short ASCII identifier-style string.
 */
const isSafeName = (name) => (
  typeof name === 'string' && name.length > 0 && name.length <= 128 && NAME_PATTERN.test(name)
);

/**
 * Record a User Timing mark. Safe to call when the API is unavailable.
 * @param {string} name - Static mark name (e.g. "portfolio-show:buildMainData:start").
 * @returns {boolean} True when the mark was recorded.
 */
export const mark = (name) => {
  const perf = getPerformance();
  if (!perf || !isSafeName(name)) return false;
  try {
    perf.mark(name);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Record a User Timing measure between two marks and clear the entries used.
 * Returning the duration lets callers log a numeric value without retaining
 * the underlying PerformanceEntry.
 * @param {string} name - Static measure name.
 * @param {string} startMark - Static mark name recorded via `mark`.
 * @param {string} finishMark - Static mark name recorded via `mark`.
 * @returns {number | null} Duration in milliseconds, or null when unsupported.
 */
export const measure = (name, startMark, finishMark) => {
  const perf = getPerformance();
  if (!perf) return null;
  if (!isSafeName(name) || !isSafeName(startMark) || !isSafeName(finishMark)) return null;
  try {
    perf.measure(name, startMark, finishMark);
    const entries = typeof perf.getEntriesByName === 'function'
      ? perf.getEntriesByName(name, 'measure')
      : [];
    const duration = entries.length > 0 ? entries[entries.length - 1].duration : null;
    perf.clearMarks(startMark);
    perf.clearMarks(finishMark);
    perf.clearMeasures(name);
    return typeof duration === 'number' ? duration : null;
  } catch (error) {
    return null;
  }
};

/**
 * Time a synchronous callback and log its duration through `logger.info`.
 * Marks and measures are cleared after logging so instrumentation does not
 * retain PerformanceEntry references.
 * @param {string} name - Static base name; ":start" and ":finish" are appended.
 * @param {Function} callbackFunc - Zero-argument synchronous callback.
 * @returns {*} The return value of `callbackFunc`.
 */
export const timeSync = (name, callbackFunc) => {
  if (typeof callbackFunc !== 'function') return undefined;
  if (!isSafeName(name)) return callbackFunc();
  const startMark = `${name}:start`;
  const finishMark = `${name}:finish`;
  const started = mark(startMark);
  try {
    return callbackFunc();
  } finally {
    if (started) {
      mark(finishMark);
      const duration = measure(name, startMark, finishMark);
      if (duration !== null) {
        logger.info(`[perf] ${name}: ${duration.toFixed(2)}ms`);
      }
    }
  }
};

/**
 * Record a duration measurement from a manually captured start timestamp.
 * Useful when the start of the interval crosses an async boundary and no
 * mark can be recorded (e.g. request settlement time). The interval is also
 * recorded as a User Timing measure so it appears in browser traces, then
 * cleared so no PerformanceEntry is retained.
 * @param {string} name - Static measure name.
 * @param {number} startTimestampMs - Value from `performance.now()`.
 * @returns {number | null} Duration in milliseconds, or null when unsupported.
 */
export const measureSince = (name, startTimestampMs) => {
  const perf = getPerformance();
  if (!perf || !isSafeName(name)) return null;
  if (typeof startTimestampMs !== 'number' || !Number.isFinite(startTimestampMs)) return null;
  const nowFn = typeof perf.now === 'function' ? () => perf.now() : null;
  if (!nowFn) return null;
  const finishTimestampMs = nowFn();
  const duration = finishTimestampMs - startTimestampMs;
  try {
    perf.measure(name, { start: startTimestampMs, end: finishTimestampMs });
    perf.clearMeasures(name);
  } catch (error) {
    // Older browsers only accept mark names; the logged duration still applies.
  }
  logger.info(`[perf] ${name}: ${duration.toFixed(2)}ms`);
  return duration;
};

/**
 * Return the current high-resolution timestamp, or null when unsupported.
 * @returns {number | null} `performance.now()` in milliseconds, or null when
 *   User Timing is unavailable.
 */
export const nowMs = () => {
  const perf = getPerformance();
  if (!perf || typeof perf.now !== 'function') return null;
  return perf.now();
};
